"use client";

import { useEffect } from "react";

import { BattleSprite } from "@/components/game/BattleSprite";
import { BattleHPBar } from "@/components/game/BattleHPBar";
import { PixelText } from "@/components/ui/pixel-text";

import { SPECIES_DEX, type SpeciesData } from "@/data/species";
import { MOVES_DATA, type MoveData } from "@/data/moves";
import { EVOLUTION_TABLE } from "@/data/evolution";
import { playSfx } from "@/engine/audio";
import {
  findPluginById,
  type Plugin,
  type PluginCapabilityCategory,
  type PluginCategory,
} from "@/data/plugins";
import {
  computeEffects,
  type PluginLoadout,
} from "@/engine/plugin-system";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type StatsScreenMember = {
  speciesId: string;
  nickname?: string;
  level: number;
  xp: number;
  hpCurrent: number;
  hpMax: number;
  /** Move ids (strings — may be numeric ids or name slugs). */
  moves: string[];
  /** Remaining PP keyed by the same move id strings. */
  pp: Record<string, number>;
  stats: { atk: number; def: number; spd: number };
  /** Defender types (lowercase). */
  types: string[];
};

type Props = {
  open: boolean;
  member: StatsScreenMember;
  onClose: () => void;
  /**
   * Optional equipped-plugin loadout. When provided, an "INSTALLED PLUGINS"
   * section + EFFECTS summary are rendered below the moves grid. When
   * omitted, the screen behaves exactly as it did before plugins existed.
   */
  loadout?: PluginLoadout;
};

// Maximum visible plugin slots in the StatsScreen plugin grid. Empty
// trailing slots render as "+" placeholders.
const PLUGIN_SLOT_COUNT = 4;

// Map plugin category to the short badge label shown beneath the name.
// The engine accepts both the new `PluginCategory` (tool/skill/capability)
// and the legacy `PluginCapabilityCategory` (growth/combat/...). We surface
// a short 3-4 letter abbreviation for either.
type AnyPluginCategory = PluginCategory | PluginCapabilityCategory;

function pluginBadge(category: AnyPluginCategory): string {
  switch (category) {
    case "tool":
      return "TOOL";
    case "skill":
      return "SKILL";
    case "capability":
      return "CAP";
    case "growth":
      return "GROW";
    case "combat":
      return "CMBT";
    case "utility":
      return "UTIL";
    case "social":
      return "SOC";
    case "economy":
      return "ECON";
  }
}

// ---------------------------------------------------------------------------
// Style constants — pulled from PIXEL_STYLE.md / BattleMoveSelect
// ---------------------------------------------------------------------------

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const WALNUT = "#5c4a30";
const SAND = "#b89968";
const MOSS = "#7a9a4a";

const TYPE_COLORS: Record<string, string> = {
  sales: "#b8704a",
  tech: "#4a7eb8",
  finance: "#c5a83a",
  creative: "#a04ab8",
  ops: "#7a8a4a",
  normal: "#8a8a8a",
};

function typeColor(type: string): string {
  return TYPE_COLORS[type.toLowerCase()] ?? TYPE_COLORS.normal;
}

function typeAbbrev(type: string): string {
  return type.slice(0, 4).toUpperCase();
}

// ---------------------------------------------------------------------------
// Species + evolution lookups
// ---------------------------------------------------------------------------

function findSpecies(speciesId: string): SpeciesData | undefined {
  const lower = speciesId.toLowerCase();
  const asNum = Number(speciesId);
  return SPECIES_DEX.find(
    (s) =>
      s.id === asNum ||
      s.name.toLowerCase() === lower ||
      s.sprite.toLowerCase() === lower,
  );
}

function getEvolutionChain(speciesId: string): SpeciesData[] {
  const start = findSpecies(speciesId);
  if (!start) return [];

  // Walk backwards to root.
  let rootId = start.id;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const prevEntry = Object.entries(EVOLUTION_TABLE).find(
      ([, v]) => v.evolvesTo === rootId,
    );
    if (!prevEntry) break;
    rootId = Number(prevEntry[0]);
  }

  // Walk forwards from root.
  const chain: SpeciesData[] = [];
  let cursor: number | undefined = rootId;
  const seen = new Set<number>();
  while (cursor !== undefined && !seen.has(cursor)) {
    seen.add(cursor);
    const sp = SPECIES_DEX.find((s) => s.id === cursor);
    if (!sp) break;
    chain.push(sp);
    cursor = EVOLUTION_TABLE[cursor]?.evolvesTo;
  }
  return chain;
}

// ---------------------------------------------------------------------------
// Moves
// ---------------------------------------------------------------------------

function findMove(moveId: string): MoveData | undefined {
  const lower = moveId.toLowerCase();
  const asNum = Number(moveId);
  return MOVES_DATA.find(
    (m) => m.id === asNum || m.name.toLowerCase() === lower,
  );
}

// ---------------------------------------------------------------------------
// XP curve — fast cubic, matches typical RPG growth.
// ---------------------------------------------------------------------------

function xpToReach(level: number): number {
  return Math.floor((4 * Math.pow(level, 3)) / 5);
}

function xpProgress(level: number, xp: number): { current: number; max: number } {
  const floor = xpToReach(level);
  const ceil = xpToReach(level + 1);
  const max = Math.max(1, ceil - floor);
  const current = Math.max(0, Math.min(max, xp - floor));
  return { current, max };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PANEL_WIDTH = 640;
const STAT_BAR_WIDTH = 220;
const STAT_BAR_HEIGHT = 10;
const STAT_NORMALIZER = 200; // bar fills at stat / 200 of the width

export function StatsScreen({ open, member, onClose, loadout }: Props) {
  // Mount/unmount sounds + keyboard.
  useEffect(() => {
    if (!open) return;
    playSfx("menu_open");

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "x" || e.key === "X") {
        e.preventDefault();
        playSfx("menu_close");
        onClose();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const species = findSpecies(member.speciesId);
  const displayName = member.nickname?.trim() || species?.name || member.speciesId;
  const chain = getEvolutionChain(member.speciesId);
  const exp = xpProgress(member.level, member.xp);
  const flavor =
    species?.description?.trim() ||
    "Often found near tall grass. Little is known about this Surggie.";

  const handleBackdropClick = () => {
    playSfx("menu_close");
    onClose();
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${displayName} stats`}
      onClick={handleBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(26, 26, 26, 0.65)",
        padding: "1rem",
      }}
    >
      <div
        onClick={stopPropagation}
        style={{
          width: `${PANEL_WIDTH}px`,
          maxWidth: "100%",
          backgroundColor: CREAM,
          border: `2px solid ${INK}`,
          boxShadow: `inset 1px 1px 0 ${CREAM}, inset -1px -1px 0 ${WALNUT}`,
          padding: "1rem 1.25rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.85rem",
          imageRendering: "pixelated",
          color: INK,
          fontFamily: "var(--font-mono, monospace)",
        }}
      >
        {/* ---------------- TOP STRIP ---------------- */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "1rem",
            borderBottom: `2px solid ${INK}`,
            paddingBottom: "0.85rem",
          }}
        >
          {/* Sprite */}
          <div style={{ flexShrink: 0 }}>
            <BattleSprite
              speciesId={member.speciesId}
              orientation="front"
              scale={3}
              state="idle"
              battlePortrait
            />
          </div>

          {/* Name + level + types */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "0.4rem",
              minWidth: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
              <PixelText
                as="div"
                glow="none"
                size="lg"
                className="!text-[#1a1a1a] !tracking-normal !normal-case"
              >
                {displayName}
              </PixelText>
              {member.nickname && species ? (
                <PixelText
                  as="span"
                  glow="none"
                  size="sm"
                  className="!text-[#5c4a30] !tracking-normal !normal-case"
                >
                  ({species.name})
                </PixelText>
              ) : null}
            </div>

            <PixelText
              as="div"
              glow="none"
              size="sm"
              className="!text-[#1a1a1a] !tracking-normal !normal-case"
            >
              Lv. {member.level}
            </PixelText>

            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
              {member.types.map((t) => (
                <span
                  key={t}
                  aria-label={`Type ${t}`}
                  style={{
                    backgroundColor: typeColor(t),
                    color: CREAM,
                    border: `2px solid ${INK}`,
                    padding: "0.15rem 0.45rem",
                    fontSize: "0.7rem",
                    lineHeight: 1,
                    letterSpacing: "0.1em",
                    fontWeight: 700,
                    imageRendering: "pixelated",
                  }}
                >
                  {typeAbbrev(t)}
                </span>
              ))}
            </div>
          </div>

          {/* Evolution chain */}
          <div
            style={{
              flexShrink: 0,
              maxWidth: "200px",
              display: "flex",
              flexDirection: "column",
              gap: "0.35rem",
            }}
          >
            <PixelText
              as="div"
              glow="none"
              size="sm"
              className="!text-[#5c4a30] !tracking-normal !normal-case"
            >
              Evolution
            </PixelText>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "0.25rem",
                fontSize: "0.75rem",
                lineHeight: 1.2,
              }}
            >
              {chain.length === 0 ? (
                <span style={{ color: WALNUT }}>—</span>
              ) : (
                chain.map((sp, idx) => {
                  const isCurrent = species?.id === sp.id;
                  const isPast =
                    species !== undefined &&
                    chain.findIndex((c) => c.id === species.id) > idx;
                  const isFuture =
                    species !== undefined &&
                    chain.findIndex((c) => c.id === species.id) < idx;

                  let color: string;
                  if (isCurrent) color = INK;
                  else if (isPast) color = WALNUT;
                  else if (isFuture) color = SAND;
                  else color = INK;

                  return (
                    <span
                      key={sp.id}
                      style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}
                    >
                      <span
                        style={{
                          color,
                          fontWeight: isCurrent ? 700 : 500,
                          opacity: isFuture ? 0.75 : 1,
                          textDecoration: isCurrent ? "underline" : "none",
                        }}
                      >
                        {sp.name}
                      </span>
                      {idx < chain.length - 1 ? (
                        <span style={{ color: WALNUT }}>{"\u2192"}</span>
                      ) : null}
                    </span>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ---------------- HP + EXP BARS ---------------- */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <BattleHPBar
            current={member.hpCurrent}
            max={member.hpMax}
            label="HP"
            width={PANEL_WIDTH - 120}
            height={10}
          />
          <ExpBarRow current={exp.current} max={exp.max} />
        </div>

        {/* ---------------- STATS BARS ---------------- */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.35rem",
            borderTop: `2px solid ${INK}`,
            paddingTop: "0.65rem",
          }}
        >
          <PixelText
            as="div"
            glow="none"
            size="sm"
            className="!text-[#1a1a1a] !tracking-normal !normal-case"
          >
            Stats
          </PixelText>
          <StatBar label="ATK" value={member.stats.atk} />
          <StatBar label="DEF" value={member.stats.def} />
          <StatBar label="SPD" value={member.stats.spd} />
        </div>

        {/* ---------------- MOVES (2x2 grid) ---------------- */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.4rem",
            borderTop: `2px solid ${INK}`,
            paddingTop: "0.65rem",
          }}
        >
          <PixelText
            as="div"
            glow="none"
            size="sm"
            className="!text-[#1a1a1a] !tracking-normal !normal-case"
          >
            Moves
          </PixelText>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0.5rem",
            }}
          >
            {[0, 1, 2, 3].map((i) => {
              const moveId = member.moves[i];
              if (!moveId) {
                return <EmptyMoveSlot key={`empty-${i}`} />;
              }
              const move = findMove(moveId);
              const ppCurrent = member.pp[moveId] ?? 0;
              const ppMax = move?.pp ?? ppCurrent;
              return (
                <MoveCard
                  key={moveId}
                  name={move?.name ?? moveId}
                  type={move?.type ?? "normal"}
                  power={move?.power ?? 0}
                  ppCurrent={ppCurrent}
                  ppMax={ppMax}
                />
              );
            })}
          </div>
        </div>

        {/* ---------------- INSTALLED PLUGINS ---------------- */}
        {loadout ? <PluginsSection loadout={loadout} /> : null}

        {/* ---------------- FLAVOR TEXT ---------------- */}
        <div
          style={{
            borderTop: `2px solid ${INK}`,
            paddingTop: "0.65rem",
            backgroundColor: CREAM,
          }}
        >
          <div
            style={{
              border: `2px solid ${INK}`,
              boxShadow: `inset 1px 1px 0 ${CREAM}, inset -1px -1px 0 ${WALNUT}`,
              padding: "0.55rem 0.75rem",
              fontSize: "0.8rem",
              lineHeight: 1.45,
              color: INK,
              backgroundColor: "#efe3c4",
            }}
          >
            {flavor}
          </div>
        </div>

        {/* ---------------- FOOTER HINT ---------------- */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.5rem",
            paddingTop: "0.25rem",
          }}
        >
          <PixelText
            as="span"
            glow="none"
            size="sm"
            className="!text-[#5c4a30] !tracking-normal !normal-case"
          >
            [X / ESC] Close
          </PixelText>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function StatBar({ label, value }: { label: string; value: number }) {
  const pct = Math.max(0, Math.min(1, value / STAT_NORMALIZER));
  const fillWidth = pct * STAT_BAR_WIDTH;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
      <span
        style={{
          width: "38px",
          fontSize: "0.75rem",
          fontWeight: 700,
          letterSpacing: "0.1em",
          color: INK,
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={STAT_NORMALIZER}
        aria-valuenow={value}
        aria-label={label}
        style={{
          position: "relative",
          width: STAT_BAR_WIDTH,
          height: STAT_BAR_HEIGHT,
          backgroundColor: CREAM,
          border: `2px solid ${INK}`,
          boxSizing: "content-box",
          imageRendering: "pixelated",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: fillWidth,
            height: STAT_BAR_HEIGHT,
            backgroundColor: INK,
          }}
        />
      </div>
      <span
        style={{
          fontSize: "0.8rem",
          fontWeight: 700,
          color: INK,
          minWidth: "30px",
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function ExpBarRow({ current, max }: { current: number; max: number }) {
  const pct = Math.max(0, Math.min(1, current / Math.max(1, max)));
  const width = PANEL_WIDTH - 120;
  const fillWidth = pct * width;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
      <PixelText
        as="span"
        glow="none"
        size="sm"
        className="!text-[#1a1a1a] !tracking-normal !normal-case"
      >
        EXP
      </PixelText>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={current}
        aria-label="EXP"
        style={{
          position: "relative",
          width,
          height: 8,
          backgroundColor: CREAM,
          border: `2px solid ${INK}`,
          boxSizing: "content-box",
          imageRendering: "pixelated",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: fillWidth,
            height: 8,
            backgroundColor: MOSS,
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: 1,
            backgroundColor: "rgba(255, 255, 255, 0.25)",
            pointerEvents: "none",
          }}
        />
      </div>
      <PixelText
        as="span"
        glow="none"
        size="sm"
        className="!text-[#1a1a1a] !tracking-normal !normal-case tabular-nums"
      >
        {Math.round(current)} / {max}
      </PixelText>
    </div>
  );
}

function MoveCard({
  name,
  type,
  power,
  ppCurrent,
  ppMax,
}: {
  name: string;
  type: string;
  power: number;
  ppCurrent: number;
  ppMax: number;
}) {
  const powerLabel = power > 0 ? `Power ${power}` : "Power \u2014";
  return (
    <div
      style={{
        position: "relative",
        backgroundColor: CREAM,
        border: `2px solid ${INK}`,
        padding: "0.5rem 0.65rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.35rem",
        color: INK,
        imageRendering: "pixelated",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem",
        }}
      >
        <span style={{ fontSize: "0.85rem", fontWeight: 700, lineHeight: 1 }}>
          {name}
        </span>
        <span
          aria-label={`Type ${type}`}
          style={{
            backgroundColor: typeColor(type),
            color: CREAM,
            border: `2px solid ${INK}`,
            padding: "0.125rem 0.4rem",
            fontSize: "0.65rem",
            lineHeight: 1,
            letterSpacing: "0.1em",
            fontWeight: 700,
            imageRendering: "pixelated",
          }}
        >
          {typeAbbrev(type)}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "0.7rem",
          lineHeight: 1,
        }}
      >
        <span>
          PP {ppCurrent}/{ppMax}
        </span>
        <span>{powerLabel}</span>
      </div>
    </div>
  );
}

function EmptyMoveSlot() {
  return (
    <div
      aria-hidden
      style={{
        backgroundColor: CREAM,
        border: `2px dashed ${WALNUT}`,
        padding: "0.5rem 0.65rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.7rem",
        color: WALNUT,
        minHeight: "48px",
        imageRendering: "pixelated",
      }}
    >
      —
    </div>
  );
}

function PluginsSection({ loadout }: { loadout: PluginLoadout }) {
  const equipped: (Plugin | undefined)[] = loadout.equipped.map((id) =>
    findPluginById(id),
  );
  // Pad up to PLUGIN_SLOT_COUNT visible slots so the user sees they can
  // install more. If more than PLUGIN_SLOT_COUNT are equipped we render
  // all of them (no truncation).
  const visibleCount = Math.max(PLUGIN_SLOT_COUNT, equipped.length);
  const slots: (Plugin | undefined)[] = [];
  for (let i = 0; i < visibleCount; i++) {
    slots.push(equipped[i]);
  }

  const summary = effectsSummary(loadout);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.4rem",
        borderTop: `2px solid ${INK}`,
        paddingTop: "0.65rem",
      }}
    >
      <PixelText
        as="div"
        glow="none"
        size="sm"
        className="!text-[#1a1a1a] !tracking-normal !normal-case"
      >
        Installed Plugins
      </PixelText>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0.5rem",
        }}
      >
        {slots.map((plugin, i) =>
          plugin ? (
            <PluginCard key={plugin.id} plugin={plugin} />
          ) : (
            <EmptyPluginSlot key={`empty-plugin-${i}`} />
          ),
        )}
      </div>
      <div
        aria-label="Plugin effects"
        style={{
          marginTop: "0.15rem",
          border: `2px solid ${INK}`,
          backgroundColor: "#efe3c4",
          color: INK,
          padding: "0.4rem 0.6rem",
          fontSize: "0.75rem",
          lineHeight: 1.3,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          fontWeight: 700,
          imageRendering: "pixelated",
        }}
      >
        <span style={{ color: WALNUT, marginRight: "0.4rem" }}>EFFECTS</span>
        {summary}
      </div>
    </div>
  );
}

function PluginCard({ plugin }: { plugin: Plugin }) {
  // Icon key — `plugin.id` is the kebab-case slug used in
  // /assets/surggies/plugins/<iconKey>.png.
  const iconSrc = `/assets/surggies/plugins/${plugin.id}.png`;
  return (
    <div
      style={{
        position: "relative",
        backgroundColor: CREAM,
        border: `2px solid ${INK}`,
        padding: "0.4rem 0.55rem",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        color: INK,
        imageRendering: "pixelated",
      }}
    >
      <div
        style={{
          width: 24,
          height: 24,
          flexShrink: 0,
          backgroundColor: "#efe3c4",
          border: `2px solid ${INK}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          imageRendering: "pixelated",
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={iconSrc}
          alt=""
          width={24}
          height={24}
          style={{
            width: 24,
            height: 24,
            display: "block",
            imageRendering: "pixelated",
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.15rem",
          minWidth: 0,
          flex: 1,
        }}
      >
        <span
          style={{
            fontSize: "0.8rem",
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {plugin.name}
        </span>
        <span
          aria-label={`Category ${plugin.category}`}
          style={{
            alignSelf: "flex-start",
            backgroundColor: WALNUT,
            color: CREAM,
            border: `2px solid ${INK}`,
            padding: "0.05rem 0.3rem",
            fontSize: "0.6rem",
            lineHeight: 1,
            letterSpacing: "0.1em",
            fontWeight: 700,
            imageRendering: "pixelated",
          }}
        >
          {pluginBadge(plugin.category)}
        </span>
      </div>
    </div>
  );
}

function EmptyPluginSlot() {
  return (
    <div
      aria-hidden
      style={{
        backgroundColor: INK,
        border: `2px solid ${INK}`,
        padding: "0.4rem 0.55rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: CREAM,
        minHeight: "44px",
        fontSize: "1rem",
        fontWeight: 700,
        lineHeight: 1,
        imageRendering: "pixelated",
      }}
    >
      +
    </div>
  );
}

// Build the EFFECTS summary line from the live `computeEffects` output and
// the loadout's category mix. Kept faithful to the engine — we do not
// fabricate atk/acc boosts that the engine does not actually grant.
function effectsSummary(loadout: PluginLoadout): string {
  const effects = computeEffects(loadout);
  const parts: string[] = [];

  const installed: Plugin[] = [];
  for (const id of loadout.equipped) {
    const p = findPluginById(id);
    if (p) installed.push(p);
  }

  // Count plugins per category to surface what the loadout emphasizes.
  const byCategory = new Map<AnyPluginCategory, number>();
  for (const p of installed) {
    byCategory.set(p.category, (byCategory.get(p.category) ?? 0) + 1);
  }

  // Surface the top 2 categories by count.
  const topCategories = Array.from(byCategory.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2);
  for (const [cat, count] of topCategories) {
    parts.push(`+${count} ${pluginBadge(cat)}`);
  }

  if (effects.unlockedEvolutions.length > 0) {
    const word =
      effects.unlockedEvolutions.length === 1 ? "evolution" : "evolutions";
    parts.push(`Unlocks ${effects.unlockedEvolutions.length} ${word}`);
  }

  if (parts.length === 0) {
    return "No active effects";
  }
  return parts.join(" \u00b7 ");
}

export default StatsScreen;
